import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import useRouter from "use-react-router";
import { Link, Route } from "react-router-dom";
import { useQuery } from "@apollo/client";
import cx from "classnames";
import homeIcon from "assets/home.svg";

import { getAppUrl } from "utilities/routes";
import { logout } from "utilities/authentication";

import Text from "components/Text";
import Icon from "components/Icon";
import variables from "styles/variables";

import styles from "./styles.scss";
import userQuery from "./userQuery.gql";
import organizationQuery from "./organization.gql";

import profileIcon from "assets/profile.svg";
import mapMarkedIcon from "assets/mapmarkedalt.svg";
import tableSolidIcon from "assets/table-solid.svg";
import calendarIcon from "assets/calendar-check.svg";
import logoutIcon from "assets/logout.svg";
import chatIcon from "assets/comment-solid.svg";
import ArrowButton from "components/ArrowButton";
import Profile from "../Profile";
import LocationTravel from "../LocationTravel";
import Calendar from "../Calendar";
import UserAvatar from "components/UserAvatar";
import { isProfileMissingInfo, isLocationMissingInfo } from "utilities/notifyMissingInfo";
import { isUserAtLeast } from "interfaces/user";
import { Maybe, SettingsHeaderOrganizationQuery, SettingsHeaderOrganizationQueryVariables, User } from "gql-gen";
import { useMobileQuery } from "components/Layout/Responsive";
import Spinner from "components/Spinner";
import { getRustBackendUrl } from "utilities/getServerRootUrl";
import { UserQueryQuery, UserQueryQueryVariables } from "gql-gen";
import { EMBED_MODE, EmbedMode } from "utilities/embed";

const i18nBase = "gogetter.settings.";

function getActiveSection(pathname: string): string {
  if (pathname.endsWith("/profile")) return "profile";
  if (pathname.endsWith("/location")) return "locationAndTravel";
  if (pathname.endsWith("/calendar")) return "calendar";
  return "";
}

export default function Settings() {
  const { t } = useTranslation();
  const {
    history,
    location,
    match: { url, params },
  } = useRouter<{ orgId: string }>();
  const orgId = params.orgId;
  const { data, loading: userLoading } = useQuery<UserQueryQuery, UserQueryQueryVariables>(userQuery);
  const activeSection = getActiveSection(location.pathname);

  const { data: orgData, loading: orgDataLoading } = useQuery<
    SettingsHeaderOrganizationQuery,
    SettingsHeaderOrganizationQueryVariables
  >(organizationQuery, { variables: { orgId } });

  const newNav = orgData?.organization?.newNav;

  return (
    <>
      <Navigation
        showNewNav={!!newNav}
        orgId={orgId}
        orgData={orgData}
        orgDataLoading={orgDataLoading}
        userFirstName={data?.me?.firstName}
        activeSection={activeSection}
      />
      <div className={cx(styles.profile, (activeSection !== "" || EMBED_MODE !== EmbedMode.NONE) && styles.hideMenu)}>
        <UserAvatar user={data?.me} square xl testId="gogetter.settings.userAvatar"></UserAvatar>
        <div className={styles.profileName} data-test="gogetter.settings.profileName">
          <Text font={"wes"} size={28}>
            {!userLoading && data?.me?.firstName}
          </Text>
          <Text font={"wes"} size={24}>
            {!userLoading && data?.me?.lastName}
          </Text>
        </div>
      </div>
      <div className={cx(styles.settingItems, activeSection !== "" && styles.hideMenu)}>
        <div
          className={styles.item}
          onClick={e => {
            e.preventDefault();
            history.push({
              ...location,
              pathname: url + `/profile`,
            });
          }}
          data-test="gogetter.settings.profileLink"
        >
          <div>
            <Icon src={profileIcon} size={20} fill={variables.gray4}></Icon>
            <Text font={"wes"} size={18}>
              {t(`${i18nBase}profile`)}
            </Text>
          </div>
          <div>
            {!userLoading && data?.me?.assets && isProfileMissingInfo(data.me) && (
              <Text bold color="pink1" size={14}>
                Missing info!
              </Text>
            )}
            <ArrowButton className={styles.arrowButton} fill={variables.gray1} size={24}></ArrowButton>
          </div>
        </div>
        <div
          className={styles.item}
          onClick={e => {
            e.preventDefault();
            history.push({
              ...location,
              pathname: url + `/location`,
            });
          }}
          data-test="gogetter.settings.locationLink"
        >
          <div>
            <Icon src={mapMarkedIcon} size={20} fill={variables.gray4}></Icon>
            <Text font={"wes"} size={18}>
              {t(`${i18nBase}locationAndTravel`)}
            </Text>
          </div>
          <div>
            {!userLoading && data?.me?.assets && isLocationMissingInfo(data.me) && (
              <Text bold color="pink1" size={14}>
                Missing info!
              </Text>
            )}
            <ArrowButton className={styles.arrowButton} fill={variables.gray1} size={24}></ArrowButton>
          </div>
        </div>
        <div
          className={styles.item}
          onClick={e => {
            e.preventDefault();
            history.push({
              ...location,
              pathname: url + `/calendar`,
            });
          }}
          data-test="gogetter.settings.calendarLink"
        >
          <div>
            <Icon src={calendarIcon} size={20} fill={variables.gray4}></Icon>
            <Text font={"wes"} size={18}>
              {t(`${i18nBase}calendar`)}
            </Text>
          </div>
          <ArrowButton className={styles.arrowButton} fill={variables.gray1} size={24}></ArrowButton>
        </div>
        <a href={getAppUrl("accounts", `${orgId ?? "_"}/my-account`, { ref: "report" })}>
          <div className={styles.item} data-test="gogetter.settings.contactAndPasswordLink">
            <div>
              <Icon src={mapMarkedIcon} size={20} fill={variables.gray4}></Icon>
              <Text font={"wes"} size={18}>
                {t(`${i18nBase}contactPassword`)}
              </Text>
            </div>
            <ArrowButton className={styles.arrowButton} fill={variables.gray1} size={24}></ArrowButton>
          </div>
        </a>
        <a href={"https://help.gopinata.com"}>
          <div className={styles.item} data-test="gogetter.settings.intercom">
            <div>
              <Icon src={chatIcon} size={20} fill={variables.gray4}></Icon>
              <Text font={"wes"} size={18} weight={100}>
                {t(`${i18nBase}helpCenter`)}
              </Text>
            </div>
            <ArrowButton className={styles.arrowButton} fill={variables.gray1} size={24}></ArrowButton>
          </div>
        </a>
        {data?.me && isUserAtLeast(data.me, "member") ? (
          <a href={getAppUrl("dashboard", `${orgId ?? "_"}`)}>
            <div className={styles.item} data-test="gogetter.settings.dashboardLink">
              <div>
                <Icon src={tableSolidIcon} size={20} fill={variables.gray4}></Icon>
                <Text font={"wes"} weight={100} size={18}>
                  {t(`${i18nBase}dashboard`)}
                </Text>
              </div>
              <ArrowButton className={styles.arrowButton} fill={variables.gray1} size={24}></ArrowButton>
            </div>
          </a>
        ) : null}
        <div
          className={styles.item}
          onClick={e => {
            e.preventDefault();
            logout();
          }}
          data-test="gogetter.settings.logoutLink"
        >
          <div>
            <Icon src={logoutIcon} size={20} fill={variables.gray4}></Icon>
            <Text font={"wes"} size={18}>
              {t(`${i18nBase}logout`)}
            </Text>
          </div>
          <ArrowButton className={styles.arrowButton} fill={variables.gray1} size={24}></ArrowButton>
        </div>
      </div>

      <div className={cx(styles.sectionContainer, activeSection === "calendar" && styles.calendarSection)}>
        <Route exact path={"/:orgId?/settings/profile"}>
          <Profile />;
        </Route>
        <Route exact path={"/:orgId?/settings/location"}>
          <LocationTravel />;
        </Route>
        <Route exact path={"/:orgId?/settings/calendar"}>
          <Calendar userId={data?.me?.id} />;
        </Route>
      </div>
    </>
  );
}

interface NewNavProps {
  orgData?: SettingsHeaderOrganizationQuery;
  orgDataLoading: boolean;
  userFirstName?: Maybe<string>;
}

function NewNav({ orgData, orgDataLoading, userFirstName }: NewNavProps) {
  const isMobile = useMobileQuery();
  const branding = orgData?.organization?.primaryColor ?? "#00DEB0";

  if (!orgData || orgDataLoading) {
    return (
      <div style={{ "--primary-color": branding } as React.CSSProperties} className={styles.newTopNav}>
        <Spinner color="white" />
      </div>
    );
  }
  const userDetailsFontSize = isMobile ? 14 : 16;

  return (
    <div style={{ "--primary-color": branding } as React.CSSProperties} className={styles.newTopNav}>
      <Logo
        url={orgData.organization.logoUrl}
        orgData={orgData}
        title={isMobile ? "Home" : orgData.organization.name}
      />
      <div className={styles.navRight}>
        <div className={styles.currentUserDetailsContainer}>
          <Text size={userDetailsFontSize} font="wes" color="white">
            {userFirstName}
          </Text>
        </div>
      </div>
    </div>
  );
}

interface LogoProps {
  url: Maybe<string | undefined>;
  orgData: SettingsHeaderOrganizationQuery;
  title: string;
}

function Logo(props: LogoProps) {
  const isMobile = useMobileQuery();

  const [imageLoaded, setImageLoaded] = useState(false);

  const redirect = () => {
    const targetUrl = new URL(getRustBackendUrl());
    targetUrl.pathname = `/api/v1/organizations/organization_users/redirect_url`;

    targetUrl.searchParams.append("org_identifier", props.orgData.organization.id);

    window.location.href = targetUrl.toString();
  };

  return (
    <div className={styles.logo}>
      <button onClick={redirect}>
        {props.url ? (
          <>
            {!imageLoaded && <Spinner color="white" />}
            <img
              src={props.url}
              onLoad={() => setImageLoaded(true)}
              height={isMobile ? 24 : 36}
              style={{ display: imageLoaded ? "block" : "none" }}
            />
          </>
        ) : (
          <Icon src={homeIcon} width={isMobile ? 24 : 36} />
        )}
        <Text font="lato" size={16} color="white">
          {props.title}
        </Text>
        <Text font="lato" size={20} color="white" align="center">
          »
        </Text>
      </button>
      <Text font="wes" size={16} color="white" className={styles.breadcrumb}>
        Settings
      </Text>
    </div>
  );
}

interface NavigationProps {
  showNewNav: boolean;
  orgData?: SettingsHeaderOrganizationQuery;
  orgId: string;
  orgDataLoading: boolean;
  userFirstName?: Maybe<string>;
  activeSection: string;
}

function Navigation({ showNewNav, orgData, orgId, orgDataLoading, userFirstName, activeSection }: NavigationProps) {
  if (EmbedMode.NONE !== EMBED_MODE) {
    return null;
  }

  const { t } = useTranslation();
  return showNewNav ? (
    <NewNav orgData={orgData} orgDataLoading={orgDataLoading} userFirstName={userFirstName} />
  ) : (
    <div className={styles.topNav}>
      <div className={styles.title}>
        <a href={orgId ? getAppUrl("go", orgId) : getAppUrl("go")}>
          <Text font={"wes"} size={12} color={"white"}>
            Go
          </Text>
        </a>

        <Link to={`/${orgId ?? "_"}/settings`}>
          <Text font={"wes"} size={12} color={"white"}>
            {t(`${i18nBase}settings`)}
          </Text>
        </Link>
        {activeSection && (
          <Text font={"wes"} size={12} color={"white"}>
            {t(`${i18nBase}${activeSection}`)}
          </Text>
        )}
      </div>
      <div
        className={styles.help}
        onClick={e => {
          e.preventDefault();
          window.open(`https://help.gopinata.com/en/collections/159994-talent`, "_blank");
        }}
      >
        <Text font={"wes"} size={12} color={"white"}>
          {t(`gogetter.help`)}
        </Text>
      </div>
    </div>
  );
}
